/**
 * ParentChip — `.parent` block from the prototype (cms-redesign-mockup.html
 * ~line 1225–1242). Two-line cell: a 24×24 type-colored icon tile + a small
 * uppercase TYPE label above the parent NAME.
 *
 *   <ParentChip type="category"    name="Anxiety Relief" />
 *   <ParentChip type="subcategory" name="Releasing Anxiety" />
 *   <ParentChip type="series"      name="Sleep Reset" />
 *   <ParentChip type="challenge"   name="21-Day Self-Love" />
 *   <ParentChip type="audiobook"   name="Pain Relief" />
 *   <ParentChip type="none" />                  // "Standalone / None"
 *
 * Per the prototype, each type has its own icon-tile tint:
 *   • category    → teal-green-dark on teal-green-soft
 *   • subcategory → terracotta #C4715B on terracotta-soft #C4715B18
 *   • series      → teal-bright rgb(var(--color-teal-bright)) on #1AA6B714
 *   • challenge   → coral-dark on coral-soft
 *   • audiobook   → reuses the subcategory tint (audiobooks are subs in v1)
 *   • none        → text-muted, no tint, smaller `close` glyph
 */
import React from 'react';
import BrandIcon from './BrandIcon';
import { cn } from './cn';

const TYPE_META = {
  category: {
    label: 'Category',
    icon: 'categories',
    iconStyle: { color: 'rgb(var(--color-teal-green-dark))', background: '#75A29318' }, // teal-d / teal-soft
  },
  subcategory: {
    label: 'Subcategory',
    icon: 'subcategory',
    iconStyle: { color: '#C4715B', background: '#C4715B18' }, // terracotta
  },
  series: {
    label: 'Series',
    icon: 'series',
    iconStyle: { color: 'rgb(var(--color-teal-bright))', background: '#1AA6B714' }, // teal-bright
  },
  challenge: {
    label: 'Challenge',
    icon: 'challenges',
    iconStyle: { color: 'rgb(var(--color-coral-dark))', background: '#E58A6A18' }, // coral-d / coral-soft
  },
  audiobook: {
    label: 'Audiobook',
    icon: 'audiobooks',
    iconStyle: { color: '#C4715B', background: '#C4715B18' }, // terracotta
  },
  none: {
    label: 'Standalone',
    icon: 'close',
    iconStyle: { color: 'rgb(var(--color-text-muted))', background: 'transparent' },
    isNone: true,
  },
  standalone: {
    label: 'Standalone',
    icon: 'close',
    iconStyle: { color: 'rgb(var(--color-text-muted))', background: 'transparent' },
    isNone: true,
  },
};

export function ParentChip({ type = 'none', name, className }) {
  const meta = TYPE_META[type] || TYPE_META.none;
  return (
    // `.parent` (1225): gap 8, navy text 13px. Renders as `flex` (not
    // inline-flex) so the chip fills its <td>'s declared width — that
    // gives the inner `.p-name truncate` something concrete to clip
    // against. Without this, a long parent name overflows the 180px
    // Parent column and bleeds into the Length cell.
    <span
      className={cn('flex items-center min-w-0 max-w-full', className)}
      style={{ gap: 8, fontSize: 13 }}
    >
      {/* `.p-icon` (1226–1231) — 24×24, sm radius (4), tinted bg, brand icon
       *  inside at currentColor. */}
      <span
        className="grid place-items-center shrink-0"
        style={{
          width: 24,
          height: 24,
          borderRadius: 4,
          ...meta.iconStyle,
        }}
      >
        <BrandIcon name={meta.icon} size={meta.isNone ? 10 : 14} />
      </span>
      {/* Two-line text — TYPE eyebrow on top, NAME below. */}
      <span className="min-w-0 flex-1">
        {/* `.p-type` (1232–1236). */}
        <span
          className="block font-condensed font-bold uppercase text-text-muted"
          style={{
            fontSize: 10,
            letterSpacing: '0.08em',
            lineHeight: 1.1,
          }}
        >
          {meta.label}
        </span>
        {/* `.p-name` (1237). When type is none/standalone, the name reads
         *  muted (1242). */}
        <span
          className={cn(
            'block truncate',
            meta.isNone ? 'text-text-muted font-medium' : 'text-navy font-semibold',
          )}
          style={{ fontSize: 13, lineHeight: 1.2 }}
        >
          {name || (meta.isNone ? 'None' : '—')}
        </span>
      </span>
    </span>
  );
}

export default ParentChip;
